import { Injectable } from '@angular/core';
import {
    LocationDetailsDto,
    LocationDto,
    LocationService,
    PersonsOrganisationDto,
    SimpleLocationDto
} from '@flyfreely-portal-ui/flyfreely';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';
import { CloneMissionLocationDialogue } from './clone-mission-location/clone-mission-location-dialogue.component';
import { LocationDetailsDialogue } from './location-details/location-details-dialogue.component';
import {
    LocationEditV2Dialogue,
    MissionAirspaceParameters
} from './location-edit-v2/location-edit-dialogue.component';
import { LocationTemplateFeaturesDialogue } from './location-template-features/location-template-features-dialogue.component';

export interface NewLocationDialogOptions {
    canCreateMission?: boolean;
    defaultValues?: Partial<LocationDetailsDto>;
    missionValues?: MissionAirspaceParameters;
}

export interface EditLocationDialogOptions {
    canCreateMission?: boolean;
    missionValues?: MissionAirspaceParameters;
    type?: LocationDetailsDto.Type | LocationDto.Type;
}

@Injectable()
export class LocationDialoguesService {
    constructor(
        private modalService: BsModalService,
        private locationService: LocationService
    ) {}

    newLocation(
        organisationId: number,
        type: LocationDetailsDto.Type | LocationDto.Type,
        options: NewLocationDialogOptions = {}
    ) {
        const {
            canCreateMission = false,
            defaultValues = null,
            missionValues = null
        } = options;

        const modal = this.modalService.show(LocationEditV2Dialogue, {
            ...MODAL_OPTIONS,
            class: 'modal-lg',
            initialState: {
                organisationId,
                type,
                defaultValues,
                missionValues,
                canCreateMission
            }
        });

        return modal;
    }

    cloneLocation(
        location: SimpleLocationDto,
        currentOrganisation: PersonsOrganisationDto
    ) {
        return this.locationService
            .findLocation(location.id, currentOrganisation.id)
            .pipe(take(1))
            .subscribe(loadedLocation =>
                this.newLocation(
                    currentOrganisation.id,
                    LocationDto.Type.TEMPLATE,
                    { canCreateMission: true, defaultValues: loadedLocation }
                )
            );
    }

    /**
     * Displays the location edit dialogue.
     *
     * @returns the promise for the dialogue
     * @param locationId
     * @param organisationId
     * @param options
     */
    editLocation(
        locationId: number,
        organisationId: number,
        options: EditLocationDialogOptions = {}
    ) {
        const {
            canCreateMission = false,
            missionValues = null,
            type
        } = options;
        const modal = this.modalService.show(LocationEditV2Dialogue, {
            ...MODAL_OPTIONS,
            class: 'modal-lg',
            initialState: {
                locationId: locationId,
                organisationId: organisationId,
                type,
                missionValues,
                canCreateMission
            }
        });

        return modal;
    }

    /**
     * Displays the location show dialogue.
     *
     * @param location the location object to display
     * @returns the promise for the dialgoue
     */
    showLocation(location: SimpleLocationDto, organisationId: number) {
        const modal = this.modalService.show(LocationDetailsDialogue, {
            ...MODAL_OPTIONS,
            class: 'modal-lg',
            initialState: {
                locationId: location.id,
                organisationId: organisationId
            }
        });

        return modal;
    }

    showMissionLocationCloneDialogue(organisationId: number) {
        return this.modalService.show(CloneMissionLocationDialogue, {
            ...MODAL_OPTIONS,
            class: 'modal-lg',
            initialState: { organisationId }
        });
    }

    showTemplateFeatureSelection(
        organisationId: number,
        templates: LocationDto[],
        template: LocationDetailsDto,
        replacesExisting: boolean
    ) {
        return this.modalService.show(LocationTemplateFeaturesDialogue, {
            ...MODAL_OPTIONS,
            class: 'modal-task-wide',
            initialState: {
                organisationId,
                templates,
                template,
                replacesExisting
            }
        });
    }
}
